import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
void motion;
import SmartImage from "../components/ui/SmartImage";

const NAV_LINKS = [
  { label: "Home",       page: "home",     path: "/" },
  { label: "Products",   page: "products", path: "/products" },
  { label: "About Us",   page: "about",    path: "/about" },
  { label: "Contact",    page: "contact",  path: "/contact" },
];

const ViewNavBar = ({ atPage }) => {
  const [isOpen, setIsOpen]     = useState(false);
  const [shrink, setShrink]     = useState(false);
  const navigate                = useNavigate();

  useEffect(() => {
    const onScroll = () => {
      const nextShrink = window.scrollY > 20;
      setShrink((current) => (current === nextShrink ? current : nextShrink));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 768) setIsOpen(false); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleNav = (link) => {
    setIsOpen(false);
    navigate(link.path, link.state ? { state: link.state } : undefined);
  };

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-blue-700 focus:shadow-lg"
      >
        Skip to content
      </a>
      {/* ── Fixed bar ──────────────────────────────────────────────────────── */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 transition-shadow duration-300 ${
          shrink ? "shadow-md" : "shadow-sm"
        }`}
      >
        <div
          className={`mx-auto flex max-w-7xl items-center justify-between px-4 md:px-8 transition-all duration-300 ${
            shrink ? "py-2" : "py-3"
          }`}
        >
          {/* Logo */}
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2.5 group"
          >
            <div
              className={`flex shrink-0 items-center justify-center overflow-hidden rounded-xl border border-blue-100 bg-blue-50 transition-all duration-300 ${
                shrink ? "h-8 w-8 md:h-9 md:w-9" : "h-10 w-10 md:h-11 md:w-11"
              }`}
            >
              <SmartImage
                src="/images/favicon.png"
                alt="Ocellus"
                wrapperClassName="h-6 w-6 md:h-7 md:w-7"
                className="h-6 w-6 md:h-7 md:w-7 object-contain"
                loading="eager"
                fetchPriority="high"
                skeletonClassName="rounded-md"
              />
            </div>
            <div className="leading-none">
              <span
                className={`block font-extrabold text-slate-900 transition-all duration-300 ${
                  shrink ? "text-base md:text-lg" : "text-lg md:text-xl"
                }`}
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                Ocellus
              </span>
              <span className="block text-[10px] font-medium tracking-wide text-slate-500 md:text-[11px]">
                Security Systems &amp; Integration
              </span>
            </div>
          </button>

          {/* Desktop links */}
          <nav
            aria-label="Primary navigation"
            className="hidden md:flex items-center gap-0.5"
          >
            {NAV_LINKS.map((link) => {
              const isActive = atPage === link.page;
              return (
                <button
                  type="button"
                  key={link.label}
                  onClick={() => handleNav(link)}
                  aria-current={isActive ? "page" : undefined}
                  className={`relative rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? "text-blue-700"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  }`}
                >
                  {link.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute bottom-0.5 left-4 right-4 h-0.5 rounded-full bg-blue-600"
                    />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="tel:+919967676810"
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors duration-200 hover:border-slate-300 hover:text-slate-900"
            >
              +91 9967676810
            </a>
            <button
              type="button"
              onClick={() => navigate("/contact")}
              className="rounded-xl bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-sm shadow-blue-100 transition-colors duration-200 hover:bg-blue-700"
            >
              Request a Consultation
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setIsOpen((v) => !v)}
            className="flex md:hidden h-9 w-9 flex-col items-center justify-center gap-1.25 rounded-xl border border-slate-200 bg-white"
            aria-label="Toggle menu"
            aria-expanded={isOpen}
            aria-controls="mobile-navigation"
          >
            <motion.span
              animate={isOpen ? { rotate: 45, y: 7 } : { rotate: 0, y: 0 }}
              transition={{ duration: 0.18 }}
              className="block h-0.5 w-4.5 rounded-full bg-slate-700 origin-center"
            />
            <motion.span
              animate={isOpen ? { opacity: 0 } : { opacity: 1 }}
              transition={{ duration: 0.12 }}
              className="block h-0.5 w-4.5 rounded-full bg-slate-700"
            />
            <motion.span
              animate={isOpen ? { rotate: -45, y: -7 } : { rotate: 0, y: 0 }}
              transition={{ duration: 0.18 }}
              className="block h-0.5 w-4.5 rounded-full bg-slate-700 origin-center"
            />
          </button>
        </div>

        {/* Mobile drawer */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              id="mobile-navigation"
              key="drawer"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="overflow-hidden border-t border-slate-200 bg-white md:hidden"
            >
              <div className="flex flex-col px-4 py-3 gap-1">
                {NAV_LINKS.map((link) => {
                  const isActive = atPage === link.page;
                  return (
                    <button
                      type="button"
                      key={link.label}
                      onClick={() => handleNav(link)}
                      aria-current={isActive ? "page" : undefined}
                      className={`flex items-center gap-2.5 rounded-xl px-4 py-3 text-sm font-medium text-left transition-colors ${
                        isActive
                          ? "bg-blue-50 text-blue-700"
                          : "text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      {isActive && (
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600" />
                      )}
                      {link.label}
                    </button>
                  );
                })}
                <a
                  href="tel:+919967676810"
                  className="mt-1 flex items-center gap-2.5 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  +91 9967676810
                </a>
                <div className="border-t border-slate-200 mt-1 pt-3 pb-1">
                  <button
                    type="button"
                    onClick={() => { setIsOpen(false); navigate("/contact"); }}
                    className="w-full rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white hover:bg-blue-700 transition-colors"
                  >
                    Request a Consultation
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Spacer — pushes ALL page content below the fixed header */}
      <div className="h-14.25 md:h-16.25" />
    </>
  );
};

export default ViewNavBar;